// ─── WorkRoamer Engineering Standard #2 — Platform Monitor ───────────────────
//
// Lightweight observability layer that fulfils Standard #2's monitoring mandate:
//   • API latency (p50 / p95 / p99 across all fetch calls)
//   • Error rates (4xx / 5xx per endpoint prefix)
//   • Cache hit tracking (X-Cache header or nomad-cache header)
//   • Memory usage (performance.memory when available)
//   • Background job timing (integrates with AICostTracker)
//
// Implementation: patches globalThis.fetch to intercept all API calls made
// from within the nomad app. Non-blocking — failures in monitoring never
// propagate to the caller.
//
// Storage: localStorage `nomad:platform-monitor:v1` — rolling 500 events.

export interface RequestEvent {
  id:           string;
  timestamp:    string;
  method:       string;
  endpoint:     string;    // pathname only (no host, no query string)
  statusCode:   number;
  durationMs:   number;
  cacheHit:     boolean;
  isError:      boolean;
  size:         number;    // content-length bytes, 0 if unknown
}

export interface EndpointStats {
  endpoint:    string;
  count:       number;
  errorCount:  number;
  errorRate:   number;
  p50Ms:       number;
  p95Ms:       number;
  p99Ms:       number;
  avgMs:       number;
  cacheHits:   number;
  cacheHitRate: number;
}

export interface MemorySnapshot {
  usedJsHeapMb:  number | null;
  totalJsHeapMb: number | null;
  limitJsHeapMb: number | null;
  available:     boolean;
}

export interface PlatformStats {
  totalRequests:    number;
  totalErrors:      number;
  overallErrorRate: number;
  cacheHits:        number;
  cacheHitRate:     number;
  p50Ms:            number;
  p95Ms:            number;
  p99Ms:            number;
  avgMs:            number;
  requestsPerMin:   number;    // rolling 1-minute rate
  topEndpoints:     EndpointStats[];
  recentErrors:     RequestEvent[];
  memory:           MemorySnapshot;
  logSize:          number;
  monitoringActive: boolean;
}

// ─── Config ───────────────────────────────────────────────────────────────────

const STORAGE_KEY  = 'nomad:platform-monitor:v1';
const MAX_EVENTS   = 500;
const API_PREFIXES = ['/api/', '/auth/'];  // only intercept app API calls

// ─── Percentile helper ────────────────────────────────────────────────────────

function percentile(sorted: number[], p: number): number {
  if (!sorted.length) return 0;
  const i = Math.ceil((p / 100) * sorted.length) - 1;
  return sorted[Math.max(0, Math.min(i, sorted.length - 1))];
}

// ─── Storage ─────────────────────────────────────────────────────────────────

let _events: RequestEvent[] | null = null;

function uid(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

function loadEvents(): RequestEvent[] {
  if (_events !== null) return _events;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) { _events = JSON.parse(raw) as RequestEvent[]; return _events; }
  } catch { /* corrupted */ }
  _events = [];
  return _events;
}

function persistEvents(): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(_events ?? [])); }
  catch {
    // Quota — trim to half and retry
    _events = (_events ?? []).slice(-Math.floor(MAX_EVENTS / 2));
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(_events)); } catch { /* give up */ }
  }
}

function pushEvent(ev: RequestEvent): void {
  const events = loadEvents();
  events.push(ev);
  if (events.length > MAX_EVENTS) {
    _events = events.slice(events.length - MAX_EVENTS);
  }
  persistEvents();
}

// ─── Memory ───────────────────────────────────────────────────────────────────

function getMemorySnapshot(): MemorySnapshot {
  // performance.memory is non-standard (Chromium only) but useful in dev
  const perf = performance as Performance & {
    memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number };
  };
  if (perf.memory) {
    const toMb = (bytes: number) => Math.round(bytes / 1_048_576 * 10) / 10;
    return {
      usedJsHeapMb:  toMb(perf.memory.usedJSHeapSize),
      totalJsHeapMb: toMb(perf.memory.totalJSHeapSize),
      limitJsHeapMb: toMb(perf.memory.jsHeapSizeLimit),
      available: true,
    };
  }
  return { usedJsHeapMb: null, totalJsHeapMb: null, limitJsHeapMb: null, available: false };
}

// ─── Fetch Interceptor ────────────────────────────────────────────────────────

let _interceptorInstalled = false;
const _originalFetch = globalThis.fetch.bind(globalThis);

function installInterceptor(): void {
  if (_interceptorInstalled) return;
  _interceptorInstalled = true;

  globalThis.fetch = async function monitoredFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const url = typeof input === 'string' ? input
      : input instanceof URL ? input.pathname + input.search
      : (input as Request).url;

    // Only intercept API calls from this app
    const isAppApi = API_PREFIXES.some(p => url.includes(p));
    if (!isAppApi) return _originalFetch(input, init);

    let pathname = '';
    try { pathname = new URL(url, location.origin).pathname; } catch { pathname = url; }
    // Normalize: strip IDs (numeric segments and UUIDs) for grouping
    const endpoint = pathname
      .replace(/\/\d+/g, '/{id}')
      .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/{uuid}');

    const t0 = performance.now();
    let response: Response;
    try {
      response = await _originalFetch(input, init);
    } catch (err) {
      // Network-level failure
      pushEvent({
        id: uid(), timestamp: new Date().toISOString(),
        method: init?.method ?? 'GET', endpoint,
        statusCode: 0, durationMs: Math.round(performance.now() - t0),
        cacheHit: false, isError: true, size: 0,
      });
      throw err;
    }

    const durationMs = Math.round(performance.now() - t0);
    const cacheHit   = response.headers.get('x-cache') === 'HIT'
      || response.headers.get('nomad-cache') === 'hit';
    const size = parseInt(response.headers.get('content-length') ?? '0', 10) || 0;

    // Clone response — reading body is destructive, but we don't need it
    pushEvent({
      id: uid(), timestamp: new Date().toISOString(),
      method: init?.method ?? (typeof input !== 'string' && 'method' in input ? (input as Request).method : 'GET'),
      endpoint,
      statusCode: response.status,
      durationMs,
      cacheHit,
      isError: response.status >= 400,
      size,
    });

    return response;
  };
}

// ─── Public API ──────────────────────────────────────────────────────────────

export const PlatformMonitor = {
  /**
   * Start the fetch interceptor. Call once at app boot.
   * Safe to call multiple times — installs only once.
   */
  install(): void {
    installInterceptor();
  },

  isActive(): boolean {
    return _interceptorInstalled;
  },

  getStats(): PlatformStats {
    const events = loadEvents();
    const now    = Date.now();
    const oneMinAgo = now - 60_000;

    const durations = events.map(e => e.durationMs).sort((a, b) => a - b);
    const errors    = events.filter(e => e.isError);
    const hits      = events.filter(e => e.cacheHit);
    const recent1m  = events.filter(e => new Date(e.timestamp).getTime() >= oneMinAgo);

    // Per-endpoint aggregation
    const endpointMap = new Map<string, RequestEvent[]>();
    for (const ev of events) {
      const arr = endpointMap.get(ev.endpoint) ?? [];
      arr.push(ev);
      endpointMap.set(ev.endpoint, arr);
    }

    const topEndpoints: EndpointStats[] = Array.from(endpointMap.entries())
      .map(([endpoint, evs]) => {
        const sorted = evs.map(e => e.durationMs).sort((a, b) => a - b);
        const errEvs = evs.filter(e => e.isError);
        const cacheEvs = evs.filter(e => e.cacheHit);
        return {
          endpoint,
          count:       evs.length,
          errorCount:  errEvs.length,
          errorRate:   errEvs.length / evs.length,
          p50Ms:       percentile(sorted, 50),
          p95Ms:       percentile(sorted, 95),
          p99Ms:       percentile(sorted, 99),
          avgMs:       Math.round(sorted.reduce((s, v) => s + v, 0) / Math.max(1, sorted.length)),
          cacheHits:   cacheEvs.length,
          cacheHitRate: cacheEvs.length / evs.length,
        };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 12);

    return {
      totalRequests:    events.length,
      totalErrors:      errors.length,
      overallErrorRate: errors.length / Math.max(1, events.length),
      cacheHits:        hits.length,
      cacheHitRate:     hits.length / Math.max(1, events.length),
      p50Ms:  percentile(durations, 50),
      p95Ms:  percentile(durations, 95),
      p99Ms:  percentile(durations, 99),
      avgMs:  durations.length
        ? Math.round(durations.reduce((s, v) => s + v, 0) / durations.length)
        : 0,
      requestsPerMin:  recent1m.length,
      topEndpoints,
      recentErrors:    errors.slice(-10).reverse(),
      memory:          getMemorySnapshot(),
      logSize:         events.length,
      monitoringActive: _interceptorInstalled,
    };
  },

  getRecentEvents(limit = 50): RequestEvent[] {
    return loadEvents().slice(-limit).reverse();
  },

  clearLog(): void {
    _events = [];
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  },
};
